import type { PostHog } from "posthog-js";

import { withoutCaptureTrailingSlash } from "@lib/telemetry/posthogCaptureUrl";
import { captureMessage } from "@client/telemetry/sentry";

export { withoutCaptureTrailingSlash };

type SendRequestOptions = Parameters<PostHog["_send_request"]>[0];
type SendRequestResponse = Parameters<
  NonNullable<SendRequestOptions["callback"]>
>[0];

/** What we learn about one failed PostHog SDK request. */
export interface PostHogRequestFailure {
  /** Path of the request, query string dropped (e.g. `/ph/e/`). */
  path: string;
  /** HTTP status, or 0 when the browser never got a response (network error,
   * extension block, timeout abort). */
  statusCode: number;
  /** `error.name: message` for a status-0 failure, else null. */
  error: string | null;
  transport: string;
  compression: string;
  /** How many times the SDK had already retried this request. */
  retries: number;
}

const MAX_REPORTS_PER_PAGE = 10;
let reportedKeys = new Set<string>();

/** Test seam. */
export function resetRequestFailureReportingForTests(): void {
  reportedKeys = new Set();
}

function requestPath(url: string): string {
  try {
    return new URL(url, "https://invalid.local").pathname;
  } catch {
    return url.split("?")[0] ?? url;
  }
}

function describeError(error: unknown): string | null {
  if (error == null) return null;
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  return String(error);
}

/**
 * Report PostHog SDK request failures, once per distinct failure per page.
 *
 * The SDK's own `on_request_error` fires only for HTTP 4xx/5xx. The failure
 * we need to see is the other kind: a capture POST that never gets a response
 * (status 0) because something in the user's browser rejects or hangs it. The
 * SDK swallows those into its retry queue, and after ten retries drops the
 * events silently. From the outside that reads as "this user does no
 * analytics", which is exactly how a funder's whole desktop usage went
 * missing — so the failure is reported to Sentry with the request shape
 * (path, transport, compression) that distinguishes the working requests
 * from the failing ones.
 *
 * Implemented by wrapping the SDK's request method: every attempt, including
 * retries and the page-hide beacons, funnels through it, and its callback
 * carries the status. Nothing else in the SDK observes status-0 outcomes.
 * The same seam applies withoutCaptureTrailingSlash to the request URL.
 */
export function installRequestFailureReporting(
  client: PostHog,
  report: (failure: PostHogRequestFailure) => void = reportToSentry,
): void {
  if (typeof client._send_request !== "function") return;
  const send = client._send_request.bind(client);
  client._send_request = (options: SendRequestOptions) => {
    const retries =
      (options as { retriesPerformedSoFar?: number }).retriesPerformedSoFar ??
      0;
    const url = withoutCaptureTrailingSlash(options.url);
    send({
      ...options,
      url,
      callback: (response: SendRequestResponse) => {
        if (response.statusCode !== 200) {
          noteFailure(
            {
              path: requestPath(url),
              statusCode: response.statusCode,
              error: describeError(response.error),
              transport: options.transport ?? "fetch",
              compression: String(options.compression ?? "none"),
              retries,
            },
            report,
          );
        }
        options.callback?.(response);
      },
    });
  };
}

function noteFailure(
  failure: PostHogRequestFailure,
  report: (failure: PostHogRequestFailure) => void,
): void {
  const key = `${failure.path}|${failure.statusCode}|${failure.error ?? ""}`;
  if (reportedKeys.has(key) || reportedKeys.size >= MAX_REPORTS_PER_PAGE) {
    return;
  }
  reportedKeys.add(key);
  report(failure);
}

function reportToSentry(failure: PostHogRequestFailure): void {
  captureMessage("PostHog request failed", {
    level: "warning",
    tags: {
      posthog_path: failure.path,
      posthog_status: String(failure.statusCode),
      posthog_transport: failure.transport,
      posthog_compression: failure.compression,
    },
    extra: {
      error: failure.error,
      retries: failure.retries,
    },
  });
}
